/**
 * Asking for a scan.
 *
 * The browser never crawls. It writes a row to `scan_requests`; the worker on Fly claims it,
 * screens the storefront, and records the run it produced. The UI watches the row.
 *
 * That is deliberately the smallest thing that works. There is no job service and no dashboard —
 * `CLAUDE.md`'s build order does not include one, and a demo needs a scan to start from somewhere
 * other than one laptop, not a queue console.
 *
 * ## What this file does not decide
 *
 * Who may request a scan is decided by RLS, in `0012_scan_requests.sql`: insert requires
 * `is_analyst()` and `requested_by = auth.uid()`. This code passes the analyst id because the
 * policy demands it, not as a check of its own — a second place deciding access is a second place
 * to get it wrong.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import { SCAN_PHASES, type ScanPhase } from '@mintro/engine';
import { RUN_DEADLINE_MS as DEADLINE } from '@mintro/engine';
import { DEFAULT_VERTICAL, type Vertical } from '@mintro/ruleset';

/** `truncated`: the run was cut short at its time limit and kept as it stood (D-282, 0087). */
export type ScanStatus = 'queued' | 'running' | 'done' | 'truncated' | 'failed';

/**
 * How a scan actually ran — an outcome, not a request (D-040).
 *
 * Every scan is inserted as `public`; the database's insert policy refuses anything else. The
 * worker rewrites this to `screening_account` only if the sampled product pages came back
 * unserved *and* a stored merchant login then reached them. Nobody chooses it.
 *
 * It never changes GATE-002 or GATE-003 — those are decided by requests carrying no session, in
 * `runGateRules`, whose API has no parameter for one (D-039).
 */
export type ScanMode = 'public' | 'screening_account';

export interface ScanRequestSummary {
  readonly id: string;
  readonly url: string;
  readonly status: ScanStatus;
  readonly progress: string | null;
  readonly error: string | null;
  readonly runId: string | null;
  readonly createdAt: string;
  /**
   * When the worker took this request, or null while it is still queued.
   *
   * Carried so the UI can tell a run that is *working* from one that is merely *labelled*
   * `running` (D-152). A live worker refreshes this every minute, so an old value means no worker
   * is touching the row — which is a different thing to show an analyst than "in progress".
   */
  readonly claimedAt: string | null;
  /**
   * When the worker recorded the outcome, or null while it has not (D-282).
   *
   * Read so the form can tell a request that stopped a minute ago — which an analyst may still be
   * waiting on — from one that stopped yesterday, and not say *"Nothing running"* over the first.
   */
  readonly finishedAt: string | null;
  readonly mode: ScanMode;
  /**
   * Which stage of the crawl is running, and how far through it, where that is knowable (D-173).
   *
   * `phase` is null before a worker claims the request and on runs written before 0047 — those are
   * immutable and will never gain one, so a reader that finds it absent shows the progress sentence
   * alone rather than a blank phase (D-044).
   *
   * `phaseDone` and `phaseTotal` are a pair or both null. **Null is the common case**: only a phase
   * whose denominator is genuinely known at that moment carries one, which excludes discovery and
   * sign-in entirely.
   */
  readonly phase: ScanPhase | null;
  readonly phaseStartedAt: string | null;
  readonly phaseDone: number | null;
  readonly phaseTotal: number | null;
}

/**
 * The result of listing scan requests, with the failure kept (D-213).
 *
 * A read that fails must never render as the absence of what it failed to read — the rule D-036 and
 * D-200 each settled for one surface, and this file is the third place it had not been applied.
 */
export type RequestList =
  | { readonly ok: true; readonly requests: readonly ScanRequestSummary[] }
  | { readonly ok: false; readonly error: string };

export interface ScanQueue {
  /**
   * Queues a scan.
   *
   * Returns the id of the row it inserted, because that id is what makes the rest correct: the
   * report the analyst is shown must be the run *this* request produced (D-045). Returns the
   * error text rather than throwing, because the caller renders it.
   */
  request(
    url: string,
    /**
     * The vertical to screen under (D-284). Defaults to `peptides`, which is also the column's default
     * (0088). No screen offers a choice yet; the parameter exists so the request row can carry one.
     */
    vertical?: Vertical,
    /**
     * The categories an adult AI scan is declared under (0090). Ignored for any other vertical, whose
     * requests the schema holds to none.
     */
    segments?: readonly string[],
  ): Promise<{ readonly ok: true; readonly id: string } | { readonly ok: false; readonly error: string }>;
  /** The most recent requests, newest first. */
  list(limit?: number): Promise<RequestList>;
  /**
   * One request, by id.
   *
   * Separate from `list()` on purpose. Watching a specific request means asking for that request,
   * not scanning a page of recent ones and hoping it is still on it — with two analysts scanning
   * concurrently it would not be.
   *
   * **Null means the read failed, not that the request is gone.** The caller keeps waiting on
   * null; treating an unreadable row as a finished or missing one is the precondition defect this
   * project keeps finding (D-026, D-036).
   */
  get(id: string): Promise<ScanRequestSummary | null>;
  /**
   * How many queued requests were created before this one (D-173).
   *
   * A real denominator — it is a count of rows, not an estimate — so it may be shown. What it is
   * **not** is a wait: turning a position into a time needs a rate, and a rate over a sample of
   * runs that varied between 110 and 626 seconds would be a prediction dressed as arithmetic.
   *
   * Zero is not rendered. "0 ahead" invites the reader to expect a start that a busy worker may
   * still be minutes from, and the line already says what is true: waiting for a worker.
   *
   * Null means the count could not be read. Not zero — the distinction D-044 turns on.
   */
  queuePosition(id: string): Promise<number | null>;
}

export function createScanQueue(client: SupabaseClient, analystId: string): ScanQueue {
  return {
    async request(url, vertical = DEFAULT_VERTICAL, segments = []) {
      const normalised = normaliseUrl(url);
      if (normalised === null) {
        return {
          ok: false,
          error: 'That is not a URL we can crawl. Give a storefront address, like https://shop.example.',
        };
      }

      const { data, error } = await client
        .from('scan_requests')
        // Always public. The insert policy in 0014 refuses anything else, so this is the client
        // agreeing with a rule the database enforces rather than a decision made here.
        .insert({
          url: normalised,
          requested_by: analystId,
          status: 'queued',
          mode: 'public',
          vertical,
          ...(vertical === 'adult_ai' ? { segments: [...segments] } : {}),
        })
        .select('id')
        .single();

      if (error !== null) {
        return { ok: false, error: error.message };
      }

      // An insert that reports success but hands back no id leaves the caller with nothing to
      // watch, and the only thing it could do then is fall back to "whichever run is newest" —
      // the wrong report, delivered confidently. Say it failed instead.
      const id = (data as { id: string } | null)?.id;
      if (id === undefined) {
        return { ok: false, error: 'The scan was queued but its id did not come back, so it cannot be followed.' };
      }

      return { ok: true, id };
    },

    async queuePosition(id) {
      const { data: row } = await client
        .from('scan_requests')
        .select('created_at, status')
        .eq('id', id)
        .single();

      const self = row as { created_at: string; status: string } | null;
      // Only a queued request has a position. One being worked on is not waiting behind anything.
      if (self === null || self.status !== 'queued') return null;

      const { count, error } = await client
        .from('scan_requests')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'queued')
        .lt('created_at', self.created_at);

      if (error !== null || count === null) return null;
      return count;
    },

    async list(limit = 10) {
      const { data, error } = await client
        .from('scan_requests')
        .select(REQUEST_COLUMNS)
        .order('created_at', { ascending: false })
        .limit(limit);

      /*
        The failure travels out (D-213).

        This returned `[]` on an error, which the scan form rendered as *"Nothing running"* — a
        merchant's scan queue that could not be read, shown as a queue with nothing in it. Same
        class as the run list, and it would have hidden the same kind of defect.
      */
      if (error !== null) return { ok: false, error: error.message };
      if (data === null) return { ok: false, error: 'the request list came back empty-handed' };

      return { ok: true, requests: (data as RequestRow[]).map(toSummary) };
    },

    async get(id) {
      const { data, error } = await client
        .from('scan_requests')
        .select(REQUEST_COLUMNS)
        .eq('id', id)
        .maybeSingle();

      // Both branches are "could not tell", and both keep the caller waiting. `maybeSingle`
      // returns null data for a row RLS hides as well as for one that does not exist, and neither
      // is a reason to stop watching a request we hold the id of.
      if (error !== null || data === null) return null;

      return toSummary(data as RequestRow);
    },
  };
}

/* One string literal, not a concatenation: supabase-js infers the row type from the literal, and
   splitting it across lines turns the result into `GenericStringError[]`. */
// prettier-ignore
const REQUEST_COLUMNS = 'id, url, status, progress, error, run_id, created_at, claimed_at, finished_at, mode, phase, phase_started_at, phase_done, phase_total';

function toSummary(row: RequestRow): ScanRequestSummary {
  return {
    id: row.id,
    url: row.url,
    status: row.status as ScanStatus,
    progress: row.progress,
    error: row.error,
    runId: row.run_id,
    createdAt: row.created_at,
    claimedAt: row.claimed_at,
    finishedAt: row.finished_at,
    mode: row.mode as ScanMode,
    // A phase the frontend has no label for is treated as absent rather than rendered raw. The
    // database constrains the vocabulary; this is what happens if the two ever disagree.
    phase: isScanPhase(row.phase) ? row.phase : null,
    phaseStartedAt: row.phase_started_at,
    phaseDone: row.phase_done,
    phaseTotal: row.phase_total,
  };
}

const isScanPhase = (value: string | null): value is ScanPhase =>
  value !== null && (SCAN_PHASES as readonly string[]).includes(value);

interface RequestRow {
  id: string;
  url: string;
  status: string;
  progress: string | null;
  error: string | null;
  run_id: string | null;
  created_at: string;
  claimed_at: string | null;
  finished_at: string | null;
  mode: string;
  phase: string | null;
  phase_started_at: string | null;
  phase_done: number | null;
  phase_total: number | null;
}

/**
 * Accepts what an analyst actually types.
 *
 * `shop.example` gets `https://`, because nobody types a scheme and refusing it would be pedantry
 * with a check constraint behind it. Anything that is still not a parseable http(s) URL is
 * refused here rather than at the database, so the message can be a sentence instead of a
 * constraint name.
 */
export function normaliseUrl(input: string): string | null {
  const trimmed = input.trim();
  if (trimmed === '') return null;

  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const url = new URL(candidate);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (!url.hostname.includes('.')) return null;
    return url.origin;
  } catch {
    return null;
  }
}

/** True while the worker still owes an answer, which is when the UI should keep watching. */
export const isPending = (status: ScanStatus): boolean => status === 'queued' || status === 'running';

/**
 * The worker's own watchdog deadline, re-exported from `@mintro/engine` (D-152).
 *
 * **The same constant the worker enforces, not a copy of its value.** A second literal here would
 * be a rule expressed in two places, and the drift would surface as a queue that calls a healthy
 * run stalled or a stalled one healthy — the display disagreeing with the machine about what is
 * happening. A request still `running` past this has outlived the ceiling the worker holds itself
 * to, so either the worker is gone or its watchdog is not running.
 */
export { RUN_DEADLINE_MS } from '@mintro/engine';

/**
 * Whether a request is `running` in name only (D-152).
 *
 * Measured from `claimed_at`, not `created_at`: time spent waiting in the queue is not the worker
 * failing to answer, and counting it would call a request stale before anyone had picked it up.
 * A live worker refreshes `claimed_at` every minute, so a value older than the deadline means no
 * process is working on this row.
 *
 * The UI states that and nothing more. It does not say the run failed — it has not been told that,
 * and a stale claim is released and retried rather than abandoned.
 */
export function isStalled(
  request: Pick<ScanRequestSummary, 'status' | 'claimedAt'>,
  now: number = Date.now(),
): boolean {
  if (request.status !== 'running' || request.claimedAt === null) return false;
  const claimed = Date.parse(request.claimedAt);
  return Number.isFinite(claimed) && now - claimed > DEADLINE;
}
